/**
 * MobileNav — Bottom navigation bar for mobile devices.
 * issue_163: USER (pending) sees only Settings. CREATOR/ADMIN see all nav items.
 * issue_167: Align role-based filtering with Sidebar.tsx.
 *   - ORGANIZATION_ADMIN: org-visible items + organization nav items (max 5 in bar, overflow → Sidebar drawer)
 *   - ORGANIZATION_MEMBER: member nav items + Settings
 *   - CREATOR/ADMIN: all nav items
 * issue_214: Allineato l'ordine logico con la Sidebar: voci non-corso al livello
 *   principale, voci corso (Iscritti, Catalogo, Assegna) raggruppate dopo.
 *   Il drawer mobile usa direttamente la Sidebar (che ora ha il sottogruppo "Corsi").
 */

import { NavLink } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  LayoutDashboard,
  Layers,
  GraduationCap,
  Target,
  Settings,
  Building2,
  Users,
  BookOpen,
  ClipboardList,
  TrendingUp,
  Menu,
} from 'lucide-react';
import { useAuth } from '@/features/auth/auth.hook';
import { isOrganizationProfile, isPendingUser } from '@/features/auth/role-utils';

export interface MobileNavProps {
  onOpenSidebar?: () => void;
}

const items = [
  { to: '/', label: 'Home', icon: LayoutDashboard, end: true, orgVisible: true, orgAdminHidden: true },
  { to: '/decks', label: 'Unità Didattiche', icon: Layers, orgVisible: false },
  { to: '/study', label: 'Study', icon: GraduationCap, orgVisible: false },
  { to: '/goals', label: 'Goals', icon: Target, orgVisible: false },
  { to: '/settings', label: 'More', icon: Settings, orgVisible: true },
];

// issue_214: Voci organizzazione non-corso (allineate con Sidebar.tsx)
const organizationNavItems = [
  { to: '/organization', label: 'Org', icon: Building2, end: true },
  { to: '/organization/users', label: 'Utenti', icon: Users },
];

// issue_214: Voci corso-related estratte in array separato (allineato con Sidebar.tsx)
const courseNavItems = [
  { to: '/organization/enrollments', label: 'Iscritti', icon: ClipboardList },
  { to: '/organization/catalog', label: 'Catalogo', icon: BookOpen },
  { to: '/organization/enrolls', label: 'Assegna', icon: ClipboardList },
];

// issue_214: Voci organizzazione residuali (dopo il blocco Corsi)
const orgExtraNavItems = [
  { to: '/organization/audit-trail', label: 'Audit', icon: BookOpen },
  { to: '/organization/compliance', label: 'Compliance', icon: BookOpen },
];

const memberNavItems = [
  { to: '/my-courses', label: 'Corsi', icon: BookOpen, end: true },
  { to: '/my-progress', label: 'Progressi', icon: TrendingUp },
];

/** Maximum number of items visible in the bottom bar (excess → "Altro" button). */
const MAX_BOTTOM_ITEMS = 5;
const MAX_BEFORE_OVERFLOW = MAX_BOTTOM_ITEMS - 1; // 4 items + 1 "Altro"

export function MobileNav({ onOpenSidebar }: MobileNavProps = {}) {
  // Hooks must be called unconditionally — no early returns before this.
  const { user } = useAuth();

  const isPending = isPendingUser(user?.role);
  const isOrg = isOrganizationProfile(user?.role);
  const isOrgAdmin = user?.role === 'organization-admin';
  const isOrgMember = user?.role === 'organization-member';

  // --- Filter base items by role (same logic as Sidebar.tsx) ---
  const visibleItems = isPending
    ? items.filter((item) => item.to === '/settings')
    : isOrg
      ? items.filter((item) => item.orgVisible && !(isOrgAdmin && item.orgAdminHidden))
      : items;

  // --- Build the final render list based on role ---
  // issue_214: Ordine logico allineato con la Sidebar:
  //   Org → Utenti → Iscritti → Catalogo → Assegna → Audit → Compliance
  let renderItems: typeof items;

  if (isOrgAdmin) {
    // Org admin: visible base items + org items + course items + org extra items
    renderItems = [...visibleItems, ...organizationNavItems, ...courseNavItems, ...orgExtraNavItems] as typeof items;
  } else if (isOrgMember) {
    // Org member: member nav items + Settings from base items
    const settingsItem = items.filter((item) => item.to === '/settings');
    renderItems = [...memberNavItems, ...settingsItem] as typeof items;
  } else {
    renderItems = visibleItems;
  }

  // --- Enforce visual limit: max 5 items in bottom bar ---
  const hasOverflow = renderItems.length > MAX_BOTTOM_ITEMS;
  const barItems = hasOverflow
    ? renderItems.slice(0, MAX_BEFORE_OVERFLOW)
    : renderItems;

  return (
    <nav
      className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-gray-200 flex items-center justify-around h-16 safe-bottom"
      aria-label="Mobile navigation"
    >
      {barItems.map((item) => {
        const Icon = item.icon;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              clsx(
                'flex flex-col items-center justify-center gap-0.5 w-full h-full text-xs transition-colors',
                isActive ? 'text-primary-600' : 'text-gray-500',
              )
            }
          >
            {({ isActive }) => (
              <>
                <Icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </>
            )}
          </NavLink>
        );
      })}

      {/* Overflow "Altro" button — opens the Sidebar drawer */}
      {hasOverflow && (
        <button
          type="button"
          onClick={() => onOpenSidebar?.()}
          className="flex flex-col items-center justify-center gap-0.5 w-full h-full text-xs text-gray-500 transition-colors hover:text-gray-700"
          aria-label="Altre opzioni"
        >
          <Menu className="w-5 h-5" />
          <span className="font-medium">Altro</span>
        </button>
      )}
    </nav>
  );
}

export default MobileNav;